import React, { useCallback, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import DataTable from "../../../components/BI/DataTable";
import FilterBar from "../../../components/BI/FilterBar";
import ExportButtons from "../../../components/BI/ExportButtons";
import usePolling from "../../../hooks/usePolling";
import {
  fetchTransferencias,
  setTransferenciasFilters,
  setTransferenciasPagination,
  clearTransferenciasFilters,
} from "../../../slices/biSlice";

const API_URL = process.env.REACT_APP_API_URL;
const POLLING_INTERVAL = 900000; // 15 minutos

// Opciones de estado para el filtro
const ESTADO_OPTIONS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "aprobada", label: "Aprobada" },
  { value: "ejecutada", label: "Ejecutada" },
];

// Opciones de bodega destino
const BODEGA_DESTINO_OPTIONS = [
  { value: "1", label: "Heredia" },
  { value: "2", label: "Guácimo" },
  { value: "3", label: "Santa Ana" },
];

export function TransferenciasMotorPage() {
  const dispatch = useDispatch();
  const { sugerencias, total, loading, error, filters, pagination } =
    useSelector((state) => state.bi.transferencias);
  const token = useSelector((state) => state.app.user?.token);

  // Track loading state for individual action buttons
  const [actionLoading, setActionLoading] = useState({});

  // Polling: fetch transferencias cada 15 min
  const fetchData = useCallback(() => {
    return dispatch(fetchTransferencias());
  }, [dispatch]);

  const { isStale, refetch } = usePolling(fetchData, POLLING_INTERVAL);

  // ============================================================
  // Configuración de filtros para FilterBar
  // ============================================================
  const filterConfig = useMemo(
    () => [
      {
        name: "estado",
        label: "Estado",
        type: "select",
        options: ESTADO_OPTIONS,
        placeholder: "Todos los estados",
      },
      {
        name: "bodegaDestino",
        label: "Bodega Destino",
        type: "select",
        options: BODEGA_DESTINO_OPTIONS,
        placeholder: "Todas las bodegas",
      },
    ],
    []
  );

  // ============================================================
  // Handlers
  // ============================================================
  const handleFilterChange = useCallback(
    (name, value) => {
      dispatch(setTransferenciasFilters({ [name]: value || null }));
      dispatch(fetchTransferencias());
    },
    [dispatch]
  );

  const handleClearFilters = useCallback(() => {
    dispatch(clearTransferenciasFilters());
    dispatch(fetchTransferencias());
  }, [dispatch]);

  const handlePageChange = useCallback(
    (skip) => {
      dispatch(setTransferenciasPagination({ skip }));
      dispatch(fetchTransferencias());
    },
    [dispatch]
  );

  // Aprobar sugerencia: PATCH con { estado: 'aprobada' }
  const handleAprobar = useCallback(
    async (id) => {
      setActionLoading((prev) => ({ ...prev, [id]: "aprobando" }));
      try {
        const response = await fetch(
          `${API_URL}/bi/transferencias/sugerencias/${id}`,
          {
            method: "PATCH",
            headers: {
              "Content-type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ estado: "aprobada" }),
          }
        );
        if (!response.ok) {
          const errText = await response.text().catch(() => "Error");
          throw new Error(errText || `HTTP ${response.status}`);
        }
        // Refrescar datos
        dispatch(fetchTransferencias());
      } catch (err) {
        alert(`Error al aprobar: ${err.message}`);
      } finally {
        setActionLoading((prev) => ({ ...prev, [id]: null }));
      }
    },
    [token, dispatch]
  );

  // Rechazar sugerencia: PATCH con { estado: 'rechazada' }
  const handleRechazar = useCallback(
    async (id) => {
      setActionLoading((prev) => ({ ...prev, [id]: "rechazando" }));
      try {
        const response = await fetch(
          `${API_URL}/bi/transferencias/sugerencias/${id}`,
          {
            method: "PATCH",
            headers: {
              "Content-type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ estado: "rechazada" }),
          }
        );
        if (!response.ok) {
          const errText = await response.text().catch(() => "Error");
          throw new Error(errText || `HTTP ${response.status}`);
        }
        dispatch(fetchTransferencias());
      } catch (err) {
        alert(`Error al rechazar: ${err.message}`);
      } finally {
        setActionLoading((prev) => ({ ...prev, [id]: null }));
      }
    },
    [token, dispatch]
  );

  // Ejecutar sugerencia aprobada: PATCH con { estado: 'ejecutada' }
  const handleEjecutar = useCallback(
    async (id) => {
      setActionLoading((prev) => ({ ...prev, [id]: "ejecutando" }));
      try {
        const response = await fetch(
          `${API_URL}/bi/transferencias/sugerencias/${id}`,
          {
            method: "PATCH",
            headers: {
              "Content-type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ estado: "ejecutada" }),
          }
        );
        if (!response.ok) {
          const errText = await response.text().catch(() => "Error");
          throw new Error(errText || `HTTP ${response.status}`);
        }
        dispatch(fetchTransferencias());
      } catch (err) {
        alert(`Error al ejecutar: ${err.message}`);
      } finally {
        setActionLoading((prev) => ({ ...prev, [id]: null }));
      }
    },
    [token, dispatch]
  );

  // URL de exportación con filtros actuales
  const exportUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.estado) params.set("estado", filters.estado);
    if (filters.bodegaDestino) params.set("bodegaDestino", filters.bodegaDestino);
    const qs = params.toString();
    return `${API_URL}/bi/transferencias/sugerencias/export/excel${qs ? `?${qs}` : ""}`;
  }, [filters]);

  // ============================================================
  // Columnas de la tabla
  // ============================================================
  const columns = useMemo(
    () => [
      { key: "nombreProducto", label: "Producto", sortable: true },
      { key: "bodegaOrigen", label: "Bodega Origen", sortable: true },
      { key: "bodegaDestino", label: "Bodega Destino", sortable: true },
      { key: "cantidad", label: "Cantidad", sortable: true },
      {
        key: "coberturaActualDias",
        label: "Cobertura Actual (días)",
        sortable: true,
        render: (row) =>
          row.coberturaActualDias != null
            ? Number(row.coberturaActualDias).toFixed(1)
            : "–",
      },
      {
        key: "coberturaObjetivoDias",
        label: "Cobertura Objetivo (días)",
        sortable: true,
        render: (row) =>
          row.coberturaObjetivoDias != null
            ? Number(row.coberturaObjetivoDias).toFixed(1)
            : "–",
      },
      {
        key: "estado",
        label: "Estado",
        sortable: true,
        render: (row) => {
          const badgeColors = {
            pendiente: "bg-yellow text-black",
            aprobada: "bg-cyan text-black",
            ejecutada: "bg-green text-white",
            rechazada: "bg-pink text-white",
          };
          const colorClass =
            badgeColors[row.estado] || "bg-gray text-baseGray";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${colorClass}`}
            >
              {row.estado}
            </span>
          );
        },
      },
      {
        key: "acciones",
        label: "Acciones",
        render: (row) => {
          const isLoading = actionLoading[row.id];

          // Pendiente: show approve and reject buttons
          if (row.estado === "pendiente") {
            return (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleAprobar(row.id)}
                  disabled={!!isLoading}
                  className="px-3 py-1 text-xs font-medium text-white bg-green rounded hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading === "aprobando" ? "..." : "Aprobar"}
                </button>
                <button
                  onClick={() => handleRechazar(row.id)}
                  disabled={!!isLoading}
                  className="px-3 py-1 text-xs font-medium text-white bg-pink rounded hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading === "rechazando" ? "..." : "Rechazar"}
                </button>
              </div>
            );
          }

          // Aprobada: show execute button
          if (row.estado === "aprobada") {
            return (
              <button
                onClick={() => handleEjecutar(row.id)}
                disabled={!!isLoading}
                className="px-3 py-1 text-xs font-medium text-white bg-purple rounded hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading === "ejecutando" ? "..." : "Ejecutar"}
              </button>
            );
          }

          // Ejecutada or other states: no actions
          return (
            <span className="text-xs text-baseGray">—</span>
          );
        },
      },
    ],
    [actionLoading, handleAprobar, handleRechazar, handleEjecutar]
  );

  // ============================================================
  // Render
  // ============================================================
  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h1 className="text-xl font-bold text-gray-800">
          Motor de Transferencias
        </h1>
        <div className="flex items-center gap-3">
          {isStale && (
            <span className="text-xs text-amber-600">
              Datos desactualizados
            </span>
          )}
          <button
            onClick={refetch}
            className="text-sm text-purple hover:underline"
          >
            Actualizar
          </button>
          <ExportButtons
            exportUrl={exportUrl}
            disabled={loading || sugerencias.length === 0}
          />
          <Link
            to="/inteligencia-negocios/transferencias/configuracion"
            className="px-4 py-2 text-sm font-medium text-white bg-purple rounded-lg hover:opacity-90 transition-opacity"
          >
            Configuración
          </Link>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <svg
            className="w-5 h-5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-sm">Error al cargar datos: {error}.</span>
          <button
            onClick={refetch}
            className="ml-auto text-sm text-red-700 underline hover:text-red-900"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* Filtros */}
      <FilterBar
        filters={filterConfig}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
      />

      {/* Tabla de datos */}
      <DataTable
        columns={columns}
        data={sugerencias}
        loading={loading}
        emptyMessage="No hay sugerencias de transferencia disponibles."
        pagination={{ limit: pagination.limit, skip: pagination.skip, total }}
        onPageChange={handlePageChange}
        pageOptions={[50]}
      />
    </div>
  );
}

export default TransferenciasMotorPage;
